import { Plugin } from 'ckeditor5/src/core';
import { ButtonView } from 'ckeditor5/src/ui';
import damIcon from '../../theme/icons/dam-icon.svg';

// Define an interface for Keepeek configuration
interface KeepekConfig {
	baseUrl?: string;
	jsLink?: string;
	cssLink?: string;
}

// Define interface for window methods - TypeScript interface augmentation
declare global {
	interface Window {
		handleSelectedValue?: ( imageData: any ) => void;
		closeKeepeekWindow?: () => void;
		damPluginInterface?: {
			selectImage?: ( imageData: any ) => void;
			cancel?: () => void;
		};
		__keepeekApp?: any;
		__keepeekAppOriginalMount?: any;
		initializeKeepeekApp?: ( containerId: string ) => void;
		keepeekAppState?: any;
		keepeekInitialized?: boolean;
	}
}

/**
 * The DAM Plugin UI with direct DOM manipulation approach.
 */
export default class DAMPluginUI extends Plugin {
	/**
	 * @inheritDoc
	 */
	public static get pluginName() {
		return 'DAMPluginUI' as const;
	}

	// React app instance
	private _reactAppContainer: HTMLElement | null = null;
	private _jsLoaded: boolean = false;
	private _cssLoaded: boolean = false;

	// Properties to store original methods for proper restoration
	private _originalHandleSelectedValue: any = undefined;
	private _originalCloseKeepeekWindow: any = undefined;
	private _originalDamPluginInterface: any = undefined;

	/**
	 * @inheritDoc
	 */
	public init(): void {
		const editor = this.editor;
		const t = editor.t;

		// Register the DAM Browser button
		editor.ui.componentFactory.add( 'damBrowser', locale => {
			const buttonView = new ButtonView( locale );

			buttonView.set( {
				label: t( 'DAM Browser' ),
				icon: damIcon,
				tooltip: true
			} );

			// Execute the command when the button is clicked
			buttonView.on( 'execute', () => {
				this._showDAMBrowser();
			} );

			return buttonView;
		} );

		// Preload CSS when initializing
		this._preloadResources();
	}

	/**
	 * Preload CSS resources to avoid delay when opening
	 */
	private _preloadResources(): void {
		const keepeekConfig = this.editor.config.get( 'keepeek' ) as KeepekConfig || {};
		const cssLink = keepeekConfig.cssLink || '';

		if ( cssLink && !this._cssLoaded ) {
			// Check if already loaded
			const existingLink = document.querySelector( `link[href="${ cssLink }"]` );
			if ( existingLink ) {
				this._cssLoaded = true;
			} else {
				// Load the CSS
				this._loadStylesheet( cssLink );
				this._cssLoaded = true;
			}
		}
	}

	/**
	 * Shows the DAM Browser dialog with a direct approach.
	 */
	private _showDAMBrowser(): void {
		console.log( 'Showing DAM browser with direct approach' );

		// First, clean up any existing instances
		this._cleanupExistingInstances();

		// Create a fresh container
		this._createContainer();

		// Load the scripts and initialize
		this._loadAndInitializeApp();
	}

	/**
	 * Creates the modal container and DOM structure
	 */
	private _createContainer(): void {
		// Create modal container
		this._reactAppContainer = document.createElement( 'div' );
		this._reactAppContainer.id = 'dam-plugin-container';
		this._reactAppContainer.style.position = 'fixed';
		this._reactAppContainer.style.top = '0';
		this._reactAppContainer.style.left = '0';
		this._reactAppContainer.style.width = '100%';
		this._reactAppContainer.style.height = '100%';
		this._reactAppContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
		this._reactAppContainer.style.zIndex = '10000';
		this._reactAppContainer.style.display = 'flex';
		this._reactAppContainer.style.justifyContent = 'center';
		this._reactAppContainer.style.alignItems = 'center';

		// Create the modal content container
		const modalContent = document.createElement( 'div' );
		modalContent.style.backgroundColor = '#fff';
		modalContent.style.width = '80%';
		modalContent.style.height = '80%';
		modalContent.style.borderRadius = '8px';
		modalContent.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.3)';
		modalContent.style.overflow = 'hidden';
		modalContent.style.position = 'relative';

		// Create the React app root
		const reactAppRoot = document.createElement( 'div' );
		reactAppRoot.id = 'valeo-dam-plugin';
		reactAppRoot.style.width = '100%';
		reactAppRoot.style.height = '100%';
		reactAppRoot.style.overflow = 'auto';

		modalContent.appendChild( reactAppRoot );
		this._reactAppContainer.appendChild( modalContent );
		document.body.appendChild( this._reactAppContainer );

		// Close on clicks outside
		this._reactAppContainer.addEventListener( 'click', e => {
			if ( e.target === this._reactAppContainer ) {
				this._doThoroughCleanup();
			}
		} );
	}

	/**
	 * Loads the script and initializes the React app with a clean approach
	 */
	private _loadAndInitializeApp(): void {
		const keepeekConfig = this.editor.config.get( 'keepeek' ) as KeepekConfig || {};
		const jsUrl = keepeekConfig.jsLink || '';

		if ( !jsUrl ) {
			console.error( 'No Keepeek JS URL configured' );
			return;
		}

		// Save original global methods
		const originalHandleSelectedValue = window.handleSelectedValue;
		const originalCloseKeepeekWindow = window.closeKeepeekWindow;
		const originalDamPluginInterface = window.damPluginInterface;

		// Reset any existing React app state
		this._resetGlobalReactState();

		// Set up our own interface for isolation
		window.damPluginInterface = {
			selectImage: ( imageData: any ) => {
				console.log( 'Image selected via interface:', imageData );
				if ( imageData ) {
					const url = imageData.url || imageData.link;
					const alt = imageData.alt || imageData.title || '';
					if ( url ) {
						this._handleImageSelected( url, alt );
					}
				}
			},
			cancel: () => {
				console.log( 'Closing DAM Browser via interface' );
				this._doThoroughCleanup();
			}
		};

		// Override methods for our instance
		window.handleSelectedValue = ( imageData: any ) => {
			console.log( 'Image selected:', imageData );
			if ( imageData ) {
				const url = imageData.url || imageData.link;
				const alt = imageData.alt || imageData.title || '';
				if ( url ) {
					this._handleImageSelected( url, alt );
				}
			}

			// Important: Store the original methods to be restored later
			this._originalHandleSelectedValue = originalHandleSelectedValue;
			this._originalCloseKeepeekWindow = originalCloseKeepeekWindow;
			this._originalDamPluginInterface = originalDamPluginInterface;
		};

		window.closeKeepeekWindow = () => {
			console.log( 'Closing DAM Browser via closeKeepeekWindow' );
			this._doThoroughCleanup();

			// Important: Store the original methods to be restored later
			this._originalHandleSelectedValue = originalHandleSelectedValue;
			this._originalCloseKeepeekWindow = originalCloseKeepeekWindow;
			this._originalDamPluginInterface = originalDamPluginInterface;
		};

		// Store references to restore later
		this._originalHandleSelectedValue = originalHandleSelectedValue;
		this._originalCloseKeepeekWindow = originalCloseKeepeekWindow;
		this._originalDamPluginInterface = originalDamPluginInterface;

		// Create a completely fresh script with cache-busting
		const timestamp = Date.now();
		const uniqueJsUrl = `${ jsUrl }${ jsUrl.includes( '?' ) ? '&' : '?' }_t=${ timestamp }`;

		console.log( 'Loading fresh Keepeek script from:', uniqueJsUrl );
		const script = document.createElement( 'script' );
		script.src = uniqueJsUrl;
		script.async = true;
		script.id = 'keepeek-script-' + timestamp;

		script.onload = () => {
			console.log( 'Keepeek script loaded successfully' );

			// If the app has a specific initialization function, call it
			if ( typeof window.initializeKeepeekApp === 'function' ) {
				console.log( 'Calling initializeKeepeekApp function' );
				window.initializeKeepeekApp( 'valeo-dam-plugin' );
			} else {
				console.log( 'No initialization function found, app should auto-initialize' );
			}
		};

		script.onerror = error => {
			console.error( 'Error loading Keepeek script:', error );
			// Do cleanup on error
			this._doThoroughCleanup();
		};

		// Add script to document
		document.body.appendChild( script );

		// Store script reference for cleanup
		if ( this._reactAppContainer ) {
			this._reactAppContainer.setAttribute( 'data-script-id', script.id );
			this._reactAppContainer.setAttribute( 'data-timestamp', timestamp.toString() );
		}

		// Set a timeout to check if the app loaded correctly
		setTimeout( () => {
			if ( !this._reactAppContainer ) {
				// Already cleaned up, do nothing
				return;
			}

			// Check if container has any children
			const appRoot = document.getElementById( 'valeo-dam-plugin' );
			if ( appRoot && appRoot.children.length === 0 ) {
				console.log( 'React app did not initialize, showing fallback UI' );

				// Add a fallback UI
				const message = document.createElement( 'div' );
				message.style.padding = '20px';
				message.style.color = '#666';
				message.style.textAlign = 'center';
				message.innerHTML = `
					<h2>Keepeek DAM Browser</h2>
					<p>The browser failed to load. This might be due to a conflict with another Keepeek instance.</p>
					<div style="margin: 20px 0;">
						<button id="retry-dam-modal" style="padding: 8px 16px; margin-right: 10px; cursor: pointer;">Retry</button>
						<button id="close-dam-modal" style="padding: 8px 16px; cursor: pointer;">Close</button>
					</div>
				`;

				appRoot.appendChild( message );

				// Add retry button event
				const retryButton = document.getElementById( 'retry-dam-modal' );
				if ( retryButton ) {
					retryButton.addEventListener( 'click', () => {
						// Completely reset and try again
						this._doThoroughCleanup();
						setTimeout( () => this._showDAMBrowser(), 100 );
					} );
				}

				// Add close button event
				const closeButton = document.getElementById( 'close-dam-modal' );
				if ( closeButton ) {
					closeButton.addEventListener( 'click', () => {
						this._doThoroughCleanup();
					} );
				}
			}
		}, 2000 );
	}

	/**
	 * Reset global React state that might interfere with initialization
	 */
	private _resetGlobalReactState(): void {
		// Clear common React global state that might prevent proper reinitialization
		( window as any ).__REACT_DEVTOOLS_GLOBAL_HOOK__ = undefined;

		// Reset any existing Keepeek state
		if ( window.__keepeekApp ) {
			try {
				if ( typeof window.__keepeekApp.unmount === 'function' ) {
					window.__keepeekApp.unmount();
				}
			} catch ( e ) {
				console.warn( 'Error unmounting Keepeek app:', e );
			}
			window.__keepeekApp = undefined;
		}

		window.__keepeekAppOriginalMount = undefined;

		// Clear any other app-specific state
		window.keepeekAppState = undefined;
		window.keepeekInitialized = undefined;
	}

	/**
	 * Performs a thorough cleanup of all DAM browser resources
	 */
	private _doThoroughCleanup(): void {
		console.log( 'Performing thorough cleanup' );

		// First restore the original methods if saved
		this._restoreOriginalMethods();

		// Then clean up the DOM elements and scripts
		this._cleanupExistingInstances();

		// Then reset any React state
		this._resetGlobalReactState();

		// Remove the container completely
		if ( this._reactAppContainer && this._reactAppContainer.parentNode ) {
			this._reactAppContainer.parentNode.removeChild( this._reactAppContainer );
			this._reactAppContainer = null;
		}
	}

	/**
	 * Restores the original window methods
	 */
	private _restoreOriginalMethods(): void {
		console.log( 'Restoring original methods', {
			handleSelectedValue: !!this._originalHandleSelectedValue,
			closeKeepeekWindow: !!this._originalCloseKeepeekWindow,
			damPluginInterface: !!this._originalDamPluginInterface
		} );

		if ( this._originalHandleSelectedValue !== undefined ) {
			window.handleSelectedValue = this._originalHandleSelectedValue;
		}

		if ( this._originalCloseKeepeekWindow !== undefined ) {
			window.closeKeepeekWindow = this._originalCloseKeepeekWindow;
		}

		if ( this._originalDamPluginInterface !== undefined ) {
			window.damPluginInterface = this._originalDamPluginInterface;
		}

		// Reset stored methods
		this._originalHandleSelectedValue = undefined;
		this._originalCloseKeepeekWindow = undefined;
		this._originalDamPluginInterface = undefined;
	}

	/**
	 * Cleans up any existing DAM browser instances
	 */
	private _cleanupExistingInstances(): void {
		console.log( 'Cleaning up existing DOM elements' );

		// Force unmount all React components connected to Keepeek
		try {
			// Remove any existing containers
			const existingContainer = document.getElementById( 'dam-plugin-container' );
			if ( existingContainer ) {
				// Get script ID if available
				const scriptId = existingContainer.getAttribute( 'data-script-id' );
				if ( scriptId ) {
					const script = document.getElementById( scriptId );
					if ( script && script.parentNode ) {
						script.parentNode.removeChild( script );
					}
				}

				existingContainer.parentNode?.removeChild( existingContainer );
			}

			// Remove any existing React roots
			const reactRoots = document.querySelectorAll( '[id^="valeo-dam-plugin"]' );
			reactRoots.forEach( root => {
				if ( root.parentNode ) {
					root.parentNode.removeChild( root );
				}
			} );

			// Remove any dangling script tags from previous instances
			const scripts = document.querySelectorAll( 'script[id^="keepeek-script-"]' );
			scripts.forEach( script => {
				if ( script.parentNode ) {
					script.parentNode.removeChild( script );
				}
			} );

			// Clean up any old DOM nodes that might be lingering
			const modals = document.querySelectorAll( '.modal-backdrop, .MuiModal-root, [data-reactroot]' );
			modals.forEach( modal => {
				if ( modal.parentNode ) {
					modal.parentNode.removeChild( modal );
				}
			} );
		} catch ( e ) {
			console.error( 'Error during DOM cleanup:', e );
		}
	}

	/**
	 * Helper method to load stylesheets
	 */
	private _loadStylesheet( href: string ): void {
		const link = document.createElement( 'link' );
		link.rel = 'stylesheet';
		link.href = href;
		document.head.appendChild( link );
		console.log( 'Loaded stylesheet:', href );
	}

	/**
	 * Handles selected image from the React app
	 */
	private _handleImageSelected( url: string, alt: string ): void {
		// Trigger the custom event with the selected image data
		this.editor.fire( 'damImageSelected', {
			url,
			alt: alt || ''
		} );

		// Perform thorough cleanup
		this._doThoroughCleanup();
	}

	/**
	 * @inheritDoc
	 */
	public override destroy(): void {
		// Perform full cleanup
		this._doThoroughCleanup();

		// Call parent class destroy
		super.destroy();
	}
}
